import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { getTenantConfig } from '@/services/tenantService';
import { isProfileComplete, isIncompleteProfileFlagSet } from '@/utils/profileUtils';

const FIRST_ACCESS_PATH = '/first-access';
const FORCE_FIRST_ACCESS_PARAM = 'forceFirstAccess';
const INCOMPLETE_PROFILE_PARAM = 'incompleteProfile';

/**
 * Guard que redireciona usuários para o fluxo de primeiro acesso em dois cenários:
 *
 * 1. CONFIGS.FIRST_ACCESS=true → usuário sem documento (fluxo original)
 * 2. CONFIGS.VERIFY_INCOMPLETE_PROFILE=true → verifica se o perfil precisa de
 *    atualização. Quando o tenant configura CONFIGS.INCOMPLETE_PROFILE_FIELD,
 *    esse atributo (setado pelo backend/CMS) é a ÚNICA fonte de verdade — o
 *    redirect passa a depender só dele, ignorando isProfileComplete(). Isso
 *    evita loop quando campos como endereço estão ocultos via feature flag
 *    (ex.: ADDRESS_SECTION=false) e por isso nunca ficariam "completos".
 *    Sem CONFIGS.INCOMPLETE_PROFILE_FIELD configurado, mantém o fallback
 *    original: isProfileComplete(user, config) baseado nos campos do usuário
 *    (cenário de usuários SSO que pularam o cadastro completo).
 *
 * Para testes: ?forceFirstAccess=1 força o redirect mesmo com documento preenchido.
 */
export const FirstAccessGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useUser();

  useEffect(() => {
    if (loading || !user) return;

    const params = new URLSearchParams(location.search);
    const forceRedirect = params.get(FORCE_FIRST_ACCESS_PARAM) === '1';

    const config = getTenantConfig();
    const firstAccessEnabled = config?.CONFIGS?.FIRST_ACCESS === true;
    const verifyIncompleteProfile = config?.CONFIGS?.VERIFY_INCOMPLETE_PROFILE === true;

    if (!firstAccessEnabled && !verifyIncompleteProfile && !forceRedirect) return;

    // Não redirecionar se já estiver no fluxo de primeiro acesso
    if (location.pathname === FIRST_ACCESS_PATH) return;

    const hasDocument = user?.document && String(user.document).trim() !== '';

    // Cenário 1: sem documento (fluxo original)
    if ((firstAccessEnabled || forceRedirect) && (forceRedirect || !hasDocument)) {
      const search = forceRedirect ? `?${FORCE_FIRST_ACCESS_PARAM}=1` : '';
      navigate(`${FIRST_ACCESS_PATH}${search}`, { replace: true });
      return;
    }

    // Cenário 2: tem documento, mas o perfil precisa de atualização.
    // Se o tenant configurou INCOMPLETE_PROFILE_FIELD, esse atributo manda
    // sozinho (ignora isProfileComplete, mesmo com outros campos vazios).
    // Sem esse field configurado, cai no fallback por completude dos campos
    // (usuários SSO que pularam o cadastro completo).
    if (verifyIncompleteProfile) {
      const hasIncompleteProfileField = !!config?.CONFIGS?.INCOMPLETE_PROFILE_FIELD;
      const needsUpdate = hasIncompleteProfileField
        ? isIncompleteProfileFlagSet(user, config)
        : !isProfileComplete(user, config);

      if (needsUpdate) {
        navigate(`${FIRST_ACCESS_PATH}?${INCOMPLETE_PROFILE_PARAM}=1`, { replace: true });
      }
    }
  }, [user, loading, location.pathname, location.search, navigate]);

  return children;
};
